/**
 * Favorites Page Component
 * Displays saved providers and resources for Care Seekers
 * FR-093: System MUST provide a Favorites page with sections for Favorite Providers and Favorite Resources
 * FR-094: System MUST display favorited items with the same card information as search results
 * FR-095: System MUST allow Care Seekers to remove items from their Favorites
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Bookmark, FileText } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Badge } from '@/shared/components/ui/badge';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { useAllFavorites, useCuratedTeam, useRemoveFavoriteProfessional, useRemoveFavoriteResource } from '../hooks/useFavorites';
import { getFileUrl } from '@/shared/hooks/useFileUpload';
import { useMyConsultationRequests } from '../../consultations/hooks/useConsultations';
import { MyRequestCard } from '../../consultations/components/MyRequestCard';
import { CuratedProvidersSection } from './CuratedProvidersSection';
import { CuratedResourcesSection } from './CuratedResourcesSection';
import { SavedResourceCard } from './SavedResourceCard';
import { PreferredBadge } from './PreferredBadge';

export function FavoritesPage() {
  const navigate = useNavigate();
  const { data: favorites, isLoading } = useAllFavorites();
  const { data: curated } = useCuratedTeam();
  const { data: engagements, isLoading: engagementsLoading, error: engagementsError } = useMyConsultationRequests();
  const removeFavoriteProfessional = useRemoveFavoriteProfessional();
  const removeFavoriteResource = useRemoveFavoriteResource();
  const [removingProfessional, setRemovingProfessional] = useState<string | null>(null);
  const [removingResource, setRemovingResource] = useState<string | null>(null);

  const handleRemoveProfessional = async (professionalUserId: string) => {
    setRemovingProfessional(professionalUserId);
    try {
      await removeFavoriteProfessional.mutateAsync(professionalUserId);
    } catch (error) {
      console.error('Failed to remove professional from favorites:', error);
    } finally {
      setRemovingProfessional(null);
    }
  };

  const handleRemoveResource = async (resourceId: string) => {
    setRemovingResource(resourceId);
    try {
      await removeFavoriteResource.mutateAsync(resourceId);
    } catch (error) {
      console.error('Failed to remove resource from favorites:', error);
    } finally {
      setRemovingResource(null);
    }
  };

  const handleViewConversation = (providerUserId: string) => {
    navigate(`/messages?userId=${providerUserId}`);
  };

  const handleViewProvider = (providerId: string) => {
    navigate(`/providers/${providerId}?discoverySource=FAVORITES`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-600">Loading your favorites...</div>
      </div>
    );
  }

  const professionals = favorites?.professionals || [];
  const resources = favorites?.resources || [];
  const consultationRequests = engagements || [];
  // Feature 083: highlight saved professionals who are also bQuest Preferred providers
  const preferredIds = new Set((curated?.preferredProviders ?? []).map((p) => p.id));

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl text-gray-900 mb-2">My Team</h1>
        <p className="text-gray-600">Manage your saved professionals, resources, and provider engagements</p>
      </div>

      <Tabs defaultValue="engagements">
        <TabsList className="grid w-full grid-cols-1 md:grid-cols-3">
          <TabsTrigger value="engagements">My Engagements ({consultationRequests.length})</TabsTrigger>
          <TabsTrigger value="professionals">Professionals ({professionals.length})</TabsTrigger>
          <TabsTrigger value="resources">Resources ({resources.length})</TabsTrigger>
        </TabsList>

        {/* My Engagements Tab */}
        <TabsContent value="engagements" className="mt-6">
          {engagementsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-600">Loading engagements...</div>
            </div>
          ) : engagementsError ? (
            <Card className="p-8 text-center">
              <div className="text-red-600 mb-2">Error loading engagements</div>
              <p className="text-gray-600">Please try refreshing the page</p>
            </Card>
          ) : consultationRequests.length === 0 ? (
            <Card className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg text-gray-900 mb-2">No provider engagements yet</h3>
              <p className="text-gray-600 mb-4">
                Start by requesting a consultation from a provider's profile.
              </p>
              <Button
                className="bg-[#026857] hover:bg-[#025247]"
                onClick={() => navigate('/providers')}
              >
                Browse Providers
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {consultationRequests.map((request) => (
                <MyRequestCard
                  key={request.id}
                  request={request}
                  onViewConversation={() => handleViewConversation(request.providerUserId)}
                  onViewProvider={() => handleViewProvider(request.providerId)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Professionals Tab */}
        <TabsContent value="professionals" className="mt-6">
          {professionals.length === 0 ? (
            <Card className="p-8 text-center">
              <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg text-gray-900 mb-2">No favorites yet</h3>
              <p className="text-gray-600 mb-4">
                Start exploring and save your favorites
              </p>
              <Button className="bg-[#026857] hover:bg-[#025247]">
                Browse Providers
              </Button>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {professionals.map((favorite) => {
                const professional = favorite.professional;
                const sp = professional.serviceProvider;
                const name = professional.name || sp?.companyName || 'Unknown Professional';
                const initials = name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2);
                const detailsPath = sp?.id
                  ? `/providers/${sp.id}?discoverySource=FAVORITES`
                  : null;
                const photoUrl = professional.profilePhoto
                  ? (professional.profilePhoto.startsWith('http') ? professional.profilePhoto : getFileUrl(professional.profilePhoto))
                  : null;

                return (
                  <Card key={favorite.id} className="p-6">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <Avatar className="w-16 h-16 flex-shrink-0">
                          {photoUrl ? (
                            <img
                              src={photoUrl}
                              alt={name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <AvatarFallback className="bg-[#026857] text-white text-lg">
                              {initials}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                            {name}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2 truncate">
                            {sp?.companyName || 'N/A'}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <Badge variant="outline">{professional.role}</Badge>
                            {preferredIds.has(professional.id) && <PreferredBadge />}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveProfessional(professional.id)}
                        disabled={removingProfessional === professional.id}
                        className="flex-shrink-0"
                      >
                        <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                      </Button>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button 
                        className="flex-1 bg-[#026857] hover:bg-[#025247]"
                        onClick={() => {
                          if (detailsPath) navigate(detailsPath);
                        }}
                        disabled={!detailsPath}
                      >
                        View Details
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => navigate(`/messages?recipient=${professional.id}`)}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Contact
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* CURATED BY BQUEST — bQuest Preferred Providers (Feature 083) */}
          <CuratedProvidersSection />
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="mt-6">
          {resources.length === 0 ? (
            <Card className="p-8 text-center">
              <Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg text-gray-900 mb-2">No saved resources yet</h3>
              <p className="text-gray-600 mb-4">
                Explore our resource hub and save helpful articles and videos
              </p>
              <Button className="bg-[#026857] hover:bg-[#025247]">
                Browse Resources
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {resources.map((favorite) => (
                <SavedResourceCard
                  key={favorite.id}
                  favorite={favorite}
                  onRemove={handleRemoveResource}
                  isRemoving={removingResource === favorite.resource.id}
                />
              ))}
            </div>
          )}

          {/* CURATED BY BQUEST — bQuest Recommended Resources (Feature 083) */}
          <CuratedResourcesSection />
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}

